import { useLazyQuery, useMutation } from "@apollo/client/react"; import React, { useMemo, useEffect, useState } from 'react';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { adaptAssistanceProgramArray } from '../../../adapters/assistanceProgramAdapter';
import { TextInput } from '../../../components/textInput/TextInput';
import { useDebounce } from '../../../hooks/useDebounce';
import { ButtonOptionGroup } from '../../../components/buttonOptionGroup/ButtonOptionGroup';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import { SwitchInput } from '../../../components/switchInput/SwitchInput';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import { GET_PROGRAMS_PAGINATED } from '../../../data/graphQL/queries/programaAsistencia/getProgramsPaginated';

import { EDIT_ASSISTANCE_PROGRAM } from '../../../data/graphQL/mutations/programaAsistencia/editProgram';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { usePersistFilters } from '../../../hooks/usePersistFilters';

import './assistanceProgramContent.scss';
export const AssistanceProgramContent = ({ setChildrenAction }) => {

  const navigate = useNavigate();

  const {
    rows,
    setRows,
    programsLoading,
    handleChangePaginationStatus,
    paginationStatus,
    totalCount,
    filters,
    handleChangeFilters,
    selectedFilterByStatus
  } = useGetAssistancePrograms();

  const [getPrograms, { data: dataPrograms }] = useLazyQuery(GET_PROGRAMS_PAGINATED, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataPrograms) return;
    setRows(dataPrograms);
  }, [dataPrograms]);

  const [editProgram] = useMutation(EDIT_ASSISTANCE_PROGRAM, {
    refetchQueries: () => {
      getPrograms({
        variables: {
          skip: paginationStatus.page * paginationStatus.pageSize,
          take: paginationStatus.pageSize,
          // We cast the filter value to boolean because Activo: 0 = false, Inactivo: 1 = true
          where: {
            and: [
              selectedFilterByStatus < 2 ? { activo: { eq: !Boolean(selectedFilterByStatus) } } : {}]

          }
        }
      });
    }
  });

  useEffect(() => {
    setChildrenAction(() =>
      <div>
        <CustomButton
          onClick={() => navigate('/programas/nuevoPrograma')}>
          Nuevo
        </CustomButton>
      </div>
    );
  }, [setChildrenAction]);

  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      flex: 0.02,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <PopoverModal>
            <div className='popover-modal-content'>
              <div
                aria-hidden='true'
                onClick={
                  () => navigate('/programas/nuevoPrograma', {
                    state: {
                      name: row?.program,
                      programTypeId: row?.typeId,
                      subtypeId: row?.subtypeId,
                      active: row?.active,
                      programId: row?.programId
                    }
                  })
                }
                className='item-option clickable-option'>
                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>
              <div className='item-option'>
                <span className='toggle-action'>Activo</span>
                <SwitchInput
                  onChange={(e) => {
                    editProgram({
                      variables: {
                        idProgram: row?.programId,
                        name: row?.program,
                        idProgramSubtype: row?.subtypeId,
                        active: e.target.checked
                      }
                    });
                    handleChangeFilters('selectedFilterByStatus', e.target.checked ? 1 : 0);
                  }}
                  checked={row?.active} />

              </div>
            </div>
          </PopoverModal>);

      }
    },
    {
      field: 'type',
      headerName: 'Tipo'
    },
    {
      field: 'subType',
      headerName: 'Subtipo'
    },
    {
      field: 'program',
      headerName: 'Programa'
    },
    {
      field: 'active',
      type: 'boolean',
      headerName: 'Activo'
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns), []);

  return (
    <div className='assistance-program-container'>
      <div className='action-row'>
        <TextInput
          placeholder='Filtrar'
          value={filters?.searchFilter}
          searchIcon
          onChange={(e) => handleChangeFilters('searchFilter', e.target.value)}
          onClearButtonClick={() => handleChangeFilters('searchFilter', '')}
          width={'30%'} />

        <div className='button-group-container'>
          <ButtonOptionGroup
            options={['Activos', 'Inactivos', 'Todos']}
            onOptionClick={(v) => handleChangeFilters('selectedFilterByStatus', v)}
            selectedOption={filters?.selectedFilterByStatus} />

        </div>
      </div>
      <DataGridGenerico
        columns={columns}
        rows={rows}
        toolbar={false}
        loading={programsLoading}
        onPageChange={(page) =>
          handleChangePaginationStatus((prev) => ({
            ...prev,
            page: page
          }))
        }
        onPageSizeChange={(pageSize) =>
          handleChangePaginationStatus((prev) => ({
            ...prev,
            pageSize: pageSize,
            page: 0
          }))
        }
        pageSize={paginationStatus.pageSize}
        totalCount={totalCount}
        currentPage={paginationStatus.page} />

    </div>);

};

const useGetAssistancePrograms = () => {
  const [assistancePrograms, setAssistancePrograms] = useState([]);
  const [paginationStatus, setPaginationStatus] = useState({
    pageSize: 10,
    page: 0
  });
  const { filters, handleChangeFilters } = usePersistFilters(
    {
      searchFilter: '',
      selectedFilterByStatus: 0
    },
    'programaAsistenciaFilters'
  );

  const {
    searchFilter,
    selectedFilterByStatus
  } = filters;

  const debouncedFilterValue = useDebounce(searchFilter, 1000);

  const [getPrograms, { data: dataPrograms, loading: programsLoading }] = useLazyQuery(GET_PROGRAMS_PAGINATED, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataPrograms || programsLoading) return;
    setAssistancePrograms(dataPrograms);
  }, [dataPrograms]);

  useEffect(() => {
    if (!programsLoading) {
      getPrograms({
        variables: {
          skip: paginationStatus.page * paginationStatus.pageSize,
          take: paginationStatus.pageSize,
          // We cast the filter value to boolean because Activo: 0 = false, Inactivo: 1 = true
          where: {
            or: [
              { nombre: { contains: debouncedFilterValue } },
              { idSubTipoProgramaNavigation: { nombre: { contains: debouncedFilterValue } } },
              { idSubTipoProgramaNavigation: { idTipoProgramaNavigation: { nombre: { contains: debouncedFilterValue } } } }],

            and: [
              selectedFilterByStatus < 2 ? { activo: { eq: !Boolean(selectedFilterByStatus) } } : {}]

          }
        }
      });
    }
  }, [selectedFilterByStatus, paginationStatus, debouncedFilterValue]);

  return {
    rows: adaptAssistanceProgramArray(assistancePrograms) || [],
    setRows: setAssistancePrograms,
    programsLoading,
    handleChangePaginationStatus: setPaginationStatus,
    paginationStatus,
    totalCount: assistancePrograms?.programs?.totalCount,
    filters,
    handleChangeFilters,
    selectedFilterByStatus
  };
};